import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import {
    FaUsers,
    FaBuilding
} from 'react-icons/fa';
import InchargeLayout from './InchargeLayout';
import InchargeNavbar from './InchargeNavbar';

const MainInchargeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        engOld: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
        engNew: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
        socOld: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
        socNew: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} }
    });

    const [animatedPerc, setAnimatedPerc] = useState(0);
    const [hovered, setHovered] = useState(null); // 'eng' | 'soc' | null


    // Derived stats using useMemo to ensure they are available before hooks use them
    const { totalRegistrations, socTotal, engTotal, socPercentage } = useMemo(() => {
        const totalReg = stats.engOld.total + stats.engNew.total + stats.socOld.total + stats.socNew.total;
        const sTotal = stats.socOld.total + stats.socNew.total;
        const eTotal = stats.engOld.total + stats.engNew.total;
        const sPerc = totalReg > 0 ? (sTotal / totalReg) * 360 : 0;
        return {
            totalRegistrations: totalReg,
            socTotal: sTotal,
            engTotal: eTotal,
            socPercentage: sPerc
        };
    }, [stats]);

    const talukas = [
        "सांगली", "कल्याण", "मुरबाड", "भिवंडी", "शहापूर", "उल्हासनगर", "अंबरनाथ"
    ];

    // Animation hook
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                setAnimatedPerc(socPercentage);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading, socPercentage]);

    // Data fetching hook
    useEffect(() => {
        const dataRefs = [
            { path: 'ekamwatapzp/EngineerRegistrations', key: 'engOld' },
            { path: 'ekamwatapzp/EngineerLotteryRegistrations', key: 'engNew' },
            { path: 'ekamwatapzp/SocietyRegistrations', key: 'socOld' },
            { path: 'ekamwatapzp/SocietyLotteryRegistrations', key: 'socNew' }
        ];

        let loadedCount = 0;
        const tempStats = {
            engOld: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
            engNew: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
            socOld: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} },
            socNew: { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} }
        };

        const unsubscribers = dataRefs.map(({ path, key }) => {
            return onValue(ref(db, path), (snapshot) => {
                const data = snapshot.val() || {};
                const currentStats = { total: 0, pending: 0, approved: 0, rejected: 0, talukas: {} };

                Object.values(data).forEach(user => {
                    const status = user.status || 'Pending';
                    const taluka = user.taluka || "Unknown";

                    currentStats.total++;
                    if (status === 'Approved') currentStats.approved++;
                    else if (status === 'Rejected') currentStats.rejected++;
                    else currentStats.pending++;

                    if (!currentStats.talukas[taluka]) {
                        currentStats.talukas[taluka] = { total: 0, pending: 0, approved: 0, rejected: 0 };
                    }
                    currentStats.talukas[taluka].total++;
                    if (status === 'Approved') currentStats.talukas[taluka].approved++;
                    else if (status === 'Rejected') currentStats.talukas[taluka].rejected++;
                    else currentStats.talukas[taluka].pending++;
                });

                tempStats[key] = currentStats;
                loadedCount++;

                if (loadedCount >= 4) {
                    setStats({ ...tempStats });
                    setLoading(false);
                }
            });
        });

        return () => unsubscribers.forEach(unsub => unsub());
    }, []);

    const renderStatBox = (label, count, colorClass) => (
        <div className="flex flex-col items-center justify-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <span className={`text-xl font-semibold ${colorClass}`}>{count}</span>
            <span className="text-xs text-gray-500 font-medium">{label}</span>
        </div>
    );

    const renderCard = (title, oldData, newData) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            <div className="w-full p-4 text-center border-b border-gray-100 bg-gray-50/50 font-semibold text-gray-700">
                {title}
            </div>
            <div className="flex w-full divide-x divide-gray-100 flex-grow">
                <div className="flex-1 flex flex-col">
                    <div className="text-[11px] font-bold text-gray-400 p-2 text-center uppercase tracking-wide bg-gray-50/30">जुने</div>
                    <div className="grid grid-cols-1 divide-y divide-gray-50 flex-grow text-center">
                        {renderStatBox("एकूण नोंदणीकृत", oldData.total, "text-blue-600")}
                        {renderStatBox("प्रलंबित फाईल", oldData.pending, "text-amber-500")}
                        {renderStatBox("मंजूर फाईल", oldData.approved, "text-emerald-600")}
                        {renderStatBox("नाकारलेली फाईल", oldData.rejected, "text-rose-600")}
                    </div>
                </div>
                <div className="flex-1 flex flex-col">
                    <div className="text-[11px] font-bold text-gray-400 p-2 text-center uppercase tracking-wide bg-gray-50/30">नवीन</div>
                    <div className="grid grid-cols-1 divide-y divide-gray-50 flex-grow text-center">
                        {renderStatBox("एकूण नोंदणीकृत", newData.total, "text-blue-600")}
                        {renderStatBox("प्रलंबित फाईल", newData.pending, "text-amber-500")}
                        {renderStatBox("मंजूर फाईल", newData.approved, "text-emerald-600")}
                        {renderStatBox("नाकारलेली फाईल", newData.rejected, "text-rose-600")}
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <InchargeLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </InchargeLayout>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <InchargeNavbar />
            <div className="p-6 md:p-8 space-y-8 bg-gray-50 flex-grow">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-6 border-gray-200">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight italic">डॅशबोर्ड अहवाल</h1>
                        <p className="text-sm text-gray-500 mt-1 italic">सांगली जिल्हा परिषद कंत्राटदार नोंदणी सद्यस्थिती</p>
                    </div>
                    <div className="mt-4 md:mt-0 text-right">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block italic">सांगली जिल्हा परिषद</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {renderCard("सु. वे. अभियंता", stats.engOld, stats.engNew)}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center min-h-[400px]">
                        <h3 className="text-lg font-bold text-gray-700 mb-10 text-center tracking-tight italic">एकूण नोंदणीकृत कंत्राटदार विभागणी</h3>
                        <div className="relative w-48 h-48 md:w-56 md:h-56">
                            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90 group">
                                <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f43f5e" strokeWidth={hovered === 'soc' ? "18" : "15"} pathLength="360" strokeDasharray={`${animatedPerc} ${360 - animatedPerc}`} className="transition-all duration-500 ease-out cursor-pointer" onMouseEnter={() => setHovered('soc')} onMouseLeave={() => setHovered(null)} />
                                <circle cx="50" cy="50" r="40" fill="transparent" stroke="#2563eb" strokeWidth={hovered === 'eng' ? "18" : "15"} pathLength="360" strokeDasharray={`${360 - animatedPerc} ${animatedPerc}`} strokeDashoffset={`-${animatedPerc}`} className="transition-all duration-500 ease-out cursor-pointer" onMouseEnter={() => setHovered('eng')} onMouseLeave={() => setHovered(null)} />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                {hovered ? (
                                    <>
                                        <span className="text-2xl font-bold text-gray-800 animate-pulse italic">{hovered === 'eng' ? engTotal : socTotal}</span>
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tighter italic">{hovered === 'eng' ? 'अभियंता' : 'सोसायटी'}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-lg font-bold text-gray-400 italic">{totalRegistrations}</span>
                                        <span className="text-[9px] font-semibold text-gray-400 uppercase italic">एकूण</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="mt-8 flex gap-8 text-sm font-medium italic">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                                <span className="text-gray-600">सु.बे. अभियंता: {engTotal}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                                <span className="text-gray-600">सोसायटी: {socTotal}</span>
                            </div>
                        </div>
                    </div>

                    {renderCard("मजूर सहकारी संस्था", stats.socOld, stats.socNew)}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30">
                        <h3 className="text-lg font-bold text-gray-700 tracking-tight italic">तालुका निहाय तपशीलवार माहिती</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse italic">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase border-r w-12">#</th>
                                    <th className="p-4 text-left text-xs font-bold text-gray-500 uppercase border-r w-32">तालुका</th>
                                    <th colSpan="2" className="p-4 text-center text-xs font-bold text-gray-500 uppercase border-r border-b">सु. बे. अभियंता</th>
                                    <th colSpan="2" className="p-4 text-center text-xs font-bold text-gray-500 uppercase border-b">मजूर सहकारी संस्था</th>
                                </tr>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="border-r"></th>
                                    <th className="border-r"></th>
                                    <th className="p-2 text-center text-[11px] font-bold text-gray-400 uppercase border-r">जुने</th>
                                    <th className="p-2 text-center text-[11px] font-bold text-gray-400 uppercase border-r">नवीन</th>
                                    <th className="p-2 text-center text-[11px] font-bold text-gray-400 uppercase border-r">जुने</th>
                                    <th className="p-2 text-center text-[11px] font-bold text-gray-400 uppercase">नवीन</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {talukas.map((taluka, index) => {
                                    const renderCell = (key) => {
                                        const data = stats[key].talukas[taluka] || { total: 0, pending: 0, approved: 0, rejected: 0 };
                                        return (
                                            <td className="p-4 border-r text-xs align-top bg-white">
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between font-semibold border-b border-gray-50 pb-1 mb-1">
                                                        <span className="text-gray-500">एकूण:</span>
                                                        <span className="text-blue-600">{data.total}</span>
                                                    </div>
                                                    <div className="flex justify-between px-1">
                                                        <span className="text-gray-400 font-medium">प्रलंबित:</span>
                                                        <span className="text-amber-500 font-semibold">{data.pending}</span>
                                                    </div>
                                                    <div className="flex justify-between px-1">
                                                        <span className="text-gray-400 font-medium">मंजूर:</span>
                                                        <span className="text-emerald-500 font-semibold">{data.approved}</span>
                                                    </div>
                                                    <div className="flex justify-between px-1">
                                                        <span className="text-gray-400 font-medium">नाकारलेले:</span>
                                                        <span className="text-rose-500 font-semibold">{data.rejected}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        );
                                    };

                                    return (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4 text-center text-sm font-medium text-gray-400 border-r">{index + 1}</td>
                                            <td className="p-4 text-sm font-bold text-gray-700 border-r bg-gray-50/20">{taluka}</td>
                                            {renderCell('engOld')}
                                            {renderCell('engNew')}
                                            {renderCell('socOld')}
                                            {renderCell('socNew')}
                                        </tr>
                                    );
                                })}
                                <tr className="bg-gray-100 font-bold border-t-2 border-gray-200">
                                    <td className="p-4 border-r"></td>
                                    <td className="p-4 text-sm text-gray-600 border-r">एकूण सर्व (Total)</td>
                                    <td className="p-4 text-center text-blue-700 border-r">{stats.engOld.total}</td>
                                    <td className="p-4 text-center text-blue-700 border-r">{stats.engNew.total}</td>
                                    <td className="p-4 text-center text-rose-700 border-r">{stats.socOld.total}</td>
                                    <td className="p-4 text-center text-rose-700">{stats.socNew.total}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

};

export default MainInchargeDashboard;
